import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, MenuItem, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import * as ProductHelper from '../../../api/product/productHelper';
import { configuration, coverLayer, drainageChimneyHeight, drainageChimneyPD } from '../../../conversion/drainageChimney';
import {
    drainageTrenchPD,
    drainHeight,
    drainLineLength,
    drainPosition,
    drainPositionDepth,
    drainSingle,
    drainUnitLength,
    drainUnitWidth,
    expectedWaterTableDepth,
    fillingPercentage,
    hasDrainageChimney,
    hasDrainagePiping,
    hasWaterproofMembraneBelowDrain,
    numberOfDrainLines,
    numberOfDrains,
    productType,
    roughnessCoefficient,
    waterTableLoweringThickness,
    wheelbase,
} from '../../../conversion/drainageTrench';
import { diameter, drainPipeDiameterPD, internalDiameter } from '../../../conversion/drainPipeDiameter';
import inputRangeDrainageTrench from '../../../inputRange/drainageTrench';
import { getLanguage } from '../../../localization';
import { cleanAlert } from '../../../store/alert/alertSlice';
import { selectDrainPipe } from '../../../store/drainPipe/drainPipeSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { cleanImage } from '../../../store/Image/imageSlice';
import { setProductPreview } from '../../../store/product/productPreviewSlice';
import { selectProject, setDrainageTrenches } from '../../../store/project/projectSlice';
import { cleanResult } from '../../../store/result/resultSlice';
import { selectUser } from '../../../store/user/userSlice';
import DropDown from '../../component/DropDown';
import Input from '../../component/Input';
import Switch from '../../component/Switch';
import { EDrainageTrenchType, EPosition, ERole, EStatus, EUnitOfMeasure } from '../../enum';
import { usePrevious } from '../../hook/hook';
import {
    EDrainageCapacityType,
    IDrainageTrench,
    IDrainPipeDiameter,
    IInputDrainageChimney,
    IInputDrainageTrench,
    ISlope,
    TInputDrainageChimney,
    TInputDrainageTrench,
    TInputDrainPipeDiameter,
} from '../../iProject';
import { dialog } from '../../util';
import { getInputDrainageChimney, getInputDrainageTrench, getInputPipeDiamater } from '../util';
import {
    handleConfigurationChange,
    handleConfigurationSwap,
    handleDrainageChimneyHeightChange,
    handleDrainChange,
    handleDrainPositionDepth,
    handleDrainSingleChange,
    handleExpectedWaterTableDepthChange,
    handleImpermeableLayerDepthChange,
    handleSlopeLengthChange,
    handleSlopeWidthChange,
    handleWaterproofMembraneChange,
    handleWheelbaseChange,
} from './calculationHelper';
import ModalProduct from './ModalProduct';

interface IInputDrainageTrenchProps {}

interface IInput {
    inputDrainageTrenches: IInputDrainageTrench[];
    inputDrainageChimney: IInputDrainageChimney[];
    inputPipeDiameter: IDrainPipeDiameter[];
}

const findValidMultiple = (value: number, multipleOf: number, maxValue: number): number => {
    if (value % multipleOf === 0 && value <= maxValue) {
        return value;
    }
    const nearestValidMultiple = Math.floor(value / multipleOf) * multipleOf;
    return nearestValidMultiple <= maxValue ? nearestValidMultiple : 0;
};

const InputDrainageTrench = (props: IInputDrainageTrenchProps) => {
    const project = useAppSelector(selectProject);
    const drainPipe = useAppSelector(selectDrainPipe);
    const user = useAppSelector(selectUser);

    const dispatch = useAppDispatch();

    const fetchProductPreview = async () => {
        let res = await ProductHelper.getPaginatedProductPreviewsAsync();
        dispatch(setProductPreview(res.data.value));
    };

    let iso = getLanguage();
    let prevIso = usePrevious(iso);

    let prevDrainageTrenches = usePrevious(project.data!.drainageTrenches);

    const { t } = useTranslation();

    const [openModal, setOpenModal] = React.useState(false);
    const [drainageTrenchType, setDrainageTrenchType] = React.useState<EDrainageTrenchType>(EDrainageTrenchType.PrimaryDrainageTrench);

    let prevSlope = usePrevious(project.data?.slope);

    let timer = React.useRef<NodeJS.Timeout>();

    const drainPipeDiameters = drainPipe.data
        ?.filter(x => (user.data?.role != ERole.Standard ? x.status != EStatus.Deleted : x.status == EStatus.Public))
        .map(obj => obj.drainPipeDiameters)
        .map(diameters => diameters!.map(d => d))
        .flat()
        .filter(x => x.unitOfMeasure == project.data?.projectInfo.unitOfMeasure)
        .filter((item, index, self) => index === self.findIndex(t => t.diameter === item.diameter && t.internalDiameter === item.internalDiameter));

    let initial: IInput = {
        inputDrainageTrenches: [],
        inputDrainageChimney: [],
        inputPipeDiameter: [],
    };

    project.data!.drainageTrenches!.map(x => {
        initial.inputDrainageTrenches.push(getInputDrainageTrench(x));
        initial.inputDrainageChimney.push(getInputDrainageChimney(x.drainageChimney!));
        initial.inputPipeDiameter.push(getInputPipeDiamater(x.drainPipe!));
    });

    let hasSecondaryDrainageTrench = project.data!.drainageTrenches!.length! > 1;
    let hasSecondaryDrainageTrenchKey = 'hasSecondaryDrainageTrench';

    const formik = useFormik<IInput>({
        initialValues: initial,
        onSubmit: values => {},
    });

    const checkDrainagePipes = (inputDrainageTrenches: IInputDrainageTrench[]) => {
        if (
            hasSecondaryDrainageTrench &&
            inputDrainageTrenches[EDrainageTrenchType.SecondaryDrainageTrench].hasDrainagePiping &&
            !inputDrainageTrenches[EDrainageTrenchType.PrimaryDrainageTrench].hasDrainagePiping
        ) {
            dialog('warning', t('drainagePipesWarning'));
        }
    };

    const checkDrainHeight = (inputDrainageTrenches: IInputDrainageTrench[]) => {
        if (
            hasSecondaryDrainageTrench &&
            inputDrainageTrenches[EDrainageTrenchType.SecondaryDrainageTrench].drainHeight! > inputDrainageTrenches[EDrainageTrenchType.PrimaryDrainageTrench].drainHeight!
        ) {
            dialog('warning', t('reduceSecondaryDrainHeight'));
        }
    };

    React.useEffect(() => {
        checkDrainagePipes(formik.values.inputDrainageTrenches);
        checkDrainHeight(formik.values.inputDrainageTrenches);
    }, []);

    React.useEffect(() => {
        if ((prevIso && iso !== prevIso) || (prevDrainageTrenches && JSON.stringify(prevDrainageTrenches) !== JSON.stringify(project.data!.drainageTrenches))) {
            if (
                (prevIso && iso !== prevIso) ||
                JSON.stringify((prevDrainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench] as IDrainageTrench).product) !==
                    JSON.stringify(project.data?.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].product) ||
                (project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench] &&
                    JSON.stringify((prevDrainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench] as IDrainageTrench)?.product) !==
                        JSON.stringify(project.data?.drainageTrenches![EDrainageTrenchType.SecondaryDrainageTrench].product))
            ) {
                let inputDrainageTrenches: IInputDrainageTrench[] = [];
                let inputDrainageChimney: IInputDrainageChimney[] = [];
                let inputPipeDiameter: IDrainPipeDiameter[] = [];
                project.data!.drainageTrenches!.map(x => {
                    inputDrainageTrenches.push(getInputDrainageTrench(x));
                    inputDrainageChimney.push(getInputDrainageChimney(x.drainageChimney!));
                    inputPipeDiameter.push(getInputPipeDiamater(x.drainPipe!));
                });

                let arr = [EDrainageTrenchType.PrimaryDrainageTrench];
                if (hasSecondaryDrainageTrench) arr.push(EDrainageTrenchType.SecondaryDrainageTrench);
                arr.map(x => handleDrainChange(inputDrainageTrenches, inputDrainageChimney, x));
                setValues({ ...formik.values, inputDrainageTrenches, inputDrainageChimney, inputPipeDiameter });
                checkDrainagePipes(inputDrainageTrenches);
                checkDrainHeight(formik.values.inputDrainageTrenches);

                if (prevIso && iso !== prevIso) {
                    fetchProductPreview();
                }
            }
        }
    }, [JSON.stringify(project.data!.drainageTrenches), iso]);

    const handleChangeHasSecondaryDrainageTrench = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        hasSecondaryDrainageTrench = event.target.checked;
        let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
        let inputDrainageChimney = [...formik.values.inputDrainageChimney];
        let inputPipeDiameter = [...formik.values.inputPipeDiameter];
        if (hasSecondaryDrainageTrench) {
            inputDrainageTrenches.push(inputDrainageTrenches[0]);
            inputDrainageChimney.push(inputDrainageChimney[0]);
            inputPipeDiameter.push(inputPipeDiameter[0]);
        } else {
            inputDrainageTrenches.pop();
            inputDrainageChimney.pop();
            inputPipeDiameter.pop();
        }
        setValues({ ...formik.values, inputDrainageTrenches, inputDrainageChimney, inputPipeDiameter });
        dispatch(cleanResult());
        dispatch(cleanImage());
        dispatch(cleanAlert());
    };

    const handleChangeDrainageTrenches = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value, type, checked } = event.target;
        let isAvailableValue = true;
        if (type == 'checkbox') {
            value = checked;
        } else {
            value = parseFloat(value);
            isAvailableValue = !isNaN(value);
        }
        let updateValue = { [name]: value };
        if (isAvailableValue) {
            let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
            let inputDrainageChimney = [...formik.values.inputDrainageChimney];
            switch (name) {
                case configuration:
                    handleConfigurationSwap(inputDrainageTrenches, inputDrainageChimney, index);
                    break;
                case drainPositionDepth:
                    handleDrainPositionDepth(value as number, inputDrainageTrenches, inputDrainageChimney);
                    break;
                case wheelbase:
                    handleWheelbaseChange(value as number, inputDrainageTrenches, index);
                    break;
                case hasWaterproofMembraneBelowDrain:
                    handleWaterproofMembraneChange(value as boolean, inputDrainageTrenches);
                    break;
                case expectedWaterTableDepth:
                    handleExpectedWaterTableDepthChange(value as number, inputDrainageTrenches, index);
                    break;
                case drainSingle:
                    handleDrainSingleChange(value as boolean, inputDrainageTrenches);
                    break;
            }
            inputDrainageTrenches[index] = {
                ...inputDrainageTrenches[index],
                ...updateValue,
            };
            setValues({ ...formik.values, inputDrainageTrenches });
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    const handleChangeDrainageChimney = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value, type, checked } = event.target;
        let isAvailableValue = true;
        if (type == 'checkbox') {
            value = checked;
        } else {
            value = parseFloat(value);
            isAvailableValue = !isNaN(value);
        }
        let updateValue = { [name]: value };
        if (isAvailableValue) {
            let inputDrainageChimney = [...formik.values.inputDrainageChimney];
            let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
            switch (name) {
                case configuration:
                    handleConfigurationChange(value as number, index, inputDrainageChimney, inputDrainageTrenches);
                    break;
                case drainageChimneyHeight:
                    handleDrainageChimneyHeightChange(value as number, index, inputDrainageChimney, inputDrainageTrenches);
                    break;
            }
            inputDrainageChimney[index] = {
                ...inputDrainageChimney[index],
                ...updateValue,
            };
            setValues({ ...formik.values, inputDrainageChimney });
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    const handleChangePipeDiameter = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, index: number) => {
        let { name, value, type, checked } = event.target;
        let isAvailableValue = true;
        if (type == 'checkbox') {
            value = checked;
        } else {
            value = parseFloat(value);
            isAvailableValue = !isNaN(value);
        }
        if (isAvailableValue) {
            let inputPipeDiameter = [...formik.values.inputPipeDiameter];
            inputPipeDiameter[index] = drainPipeDiameters?.find((x, i) => i == value)!;
            setValues({ ...formik.values, inputPipeDiameter });
            dispatch(cleanResult());
            dispatch(cleanImage());
            dispatch(cleanAlert());
        }
    };

    const setValues = (payload: IInput) => {
        formik.setValues(payload);
        let drainageTrench = payload.inputDrainageTrenches.map((x, i) => {
            let drainageTrenches = project.data!.drainageTrenches?.[i] ? project.data!.drainageTrenches?.[i] : project.data!.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench];
            let drainPipeAvailable = cloneDeep(drainPipe.data);
            let drainPipeSelected = project.data?.drainageTrenches?.[i]
                ? project.data?.drainageTrenches?.[i].drainPipe
                : project.data!.drainageTrenches![EDrainageTrenchType.PrimaryDrainageTrench].drainPipe;
            if (drainPipeAvailable) {
                drainPipeSelected = drainPipeAvailable?.find(x => x.drainPipeDiameters?.some(x => x.diameter == payload.inputPipeDiameter[i].diameter));
                drainPipeSelected!.drainPipeDiameters = drainPipeSelected?.drainPipeDiameters?.filter(x => x.diameter == payload.inputPipeDiameter[i].diameter);
            }
            return {
                ...drainageTrenches,
                isPrimaryDrainageTrench: i == 0,
                ...x,
                drainageChimney: { ...drainageTrenches.drainageChimney, ...payload.inputDrainageChimney[i] },
                drainPipe: drainPipeSelected,
            };
        });
        dispatch(setDrainageTrenches(drainageTrench));

        if (
            payload.inputDrainageChimney[EDrainageTrenchType.PrimaryDrainageTrench].coverLayer! < 0.5 ||
            (hasSecondaryDrainageTrench && payload.inputDrainageChimney[EDrainageTrenchType.SecondaryDrainageTrench].coverLayer! < 0.5)
        ) {
            dialog(
                'warning',
                t('coverLayerWarning', {
                    value:
                        inputRangeDrainageTrench()['coverLayer']?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] +
                        drainageChimneyPD[coverLayer].unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                }),
            );
        }
    };

    React.useEffect(() => {
        if (prevSlope && (prevSlope as ISlope).slopeLength !== project.data?.slope?.slopeLength) {
            let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
            handleSlopeLengthChange(project.data?.slope?.slopeLength!, inputDrainageTrenches);
            setValues({ ...formik.values, inputDrainageTrenches });
        }
    }, [project.data?.slope?.slopeLength]);

    React.useEffect(() => {
        if (prevSlope && (prevSlope as ISlope).impermeableLayerDepth !== project.data?.slope?.impermeableLayerDepth) {
            let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
            handleImpermeableLayerDepthChange(project.data?.slope?.impermeableLayerDepth!, inputDrainageTrenches);
            setValues({ ...formik.values, inputDrainageTrenches });
        }
    }, [project.data?.slope?.impermeableLayerDepth]);

    React.useEffect(() => {
        if (prevSlope && (prevSlope as ISlope).slopeWidth !== project.data?.slope?.slopeWidth) {
            let inputDrainageTrenches = [...formik.values.inputDrainageTrenches];
            handleSlopeWidthChange(project.data?.slope?.slopeWidth!, inputDrainageTrenches);
            setValues({ ...formik.values, inputDrainageTrenches });
        }
    }, [project.data?.slope?.slopeWidth]);

    const handleHidden = (name: string, index: number) => {
        if (project.data?.isBase && (name == diameter || name == internalDiameter || name == hasDrainagePiping)) return true;
        if (!project.data?.isBase && !formik.values.inputDrainageTrenches[index][hasDrainagePiping] && (name == roughnessCoefficient || name == fillingPercentage)) return true;
        if (name == drainSingle) return true;
        return false;
    };

    const handleDisabled = (name: string, index: number) => {
        if (name == productType || name == drainHeight || name == drainUnitLength || name == drainUnitWidth || name == numberOfDrainLines || name == waterTableLoweringThickness || name == coverLayer)
            return true;
        if (index == EDrainageTrenchType.SecondaryDrainageTrench && (name == drainPositionDepth || name == expectedWaterTableDepth)) return true;
        if (index == EDrainageTrenchType.PrimaryDrainageTrench && name == wheelbase) return !!formik.values.inputDrainageTrenches[EDrainageTrenchType.PrimaryDrainageTrench][drainSingle];
        return false;
    };

    const handleTextTooltip = (name: string) => {
        if (name == roughnessCoefficient) return t('roughnessCoefficientInfobox');
        if (name == waterTableLoweringThickness) return t('waterTableLoweringThicknessInfobox');
        if (name == coverLayer) return t('coverLayerInfobox', { value: project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? '0.50' + ' ' + t('meters') : '' });
        if (name == waterTableLoweringThickness) return t('waterTableLoweringThicknessInfobox');
    };

    const handleClickDrainChoice = (drainageTrenchType: EDrainageTrenchType) => {
        setDrainageTrenchType(drainageTrenchType);
        handleOpenModal();
    };

    const handleCloseModal = () => setOpenModal(false);
    const handleOpenModal = () => setOpenModal(true);

    return (
        <>
            <Accordion disableGutters>
                <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel3a-content' id='panel3a-header'>
                    <Typography>{t('drainInput')}</Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: 0 }}>
                    {formik.values.inputDrainageTrenches.map((collector, i) => (
                        <Stack key={`${collector.productType}_${i}`}>
                            <Accordion defaultExpanded disableGutters>
                                <AccordionSummary variant='children' expandIcon={<ExpandMoreIcon />} aria-controls={`${collector.productType}_${i}`} id={`${collector.productType}_${i}`}>
                                    <Typography>{t('drainageTrenchesType', { type: i == 0 ? t('primary') : t('secondary') })}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack>
                                        <Button
                                            variant='green'
                                            style={{ width: '100%', margin: '0.5rem 0' }}
                                            onClick={() =>
                                                i == 0 ? handleClickDrainChoice(EDrainageTrenchType.PrimaryDrainageTrench) : handleClickDrainChoice(EDrainageTrenchType.SecondaryDrainageTrench)
                                            }>
                                            {t('drainChoice')}
                                        </Button>
                                        {Object.keys(collector).map(x => {
                                            let el;
                                            if (x === drainLineLength) {
                                                el = (
                                                    <>
                                                        <Input
                                                            value={formik.values.inputDrainageTrenches[i][drainLineLength as TInputDrainageTrench]}
                                                            id={drainLineLength}
                                                            minValue={inputRangeDrainageTrench()[drainLineLength]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                            maxValue={inputRangeDrainageTrench()[drainLineLength]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                            isNumber
                                                            label={t(drainLineLength, {
                                                                unitOfMeasure: drainageTrenchPD[drainLineLength as TInputDrainageTrench]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                            })}
                                                            name={drainLineLength}
                                                            onChange={(e: any) => handleChangeDrainageTrenches(e, i)}
                                                        />
                                                        {i === EDrainageTrenchType.PrimaryDrainageTrench && (
                                                            <Switch
                                                                defaultChecked={formik.values.inputDrainageTrenches[i][drainSingle as TInputDrainageTrench] as boolean}
                                                                onChange={e => handleChangeDrainageTrenches(e, i)}
                                                                name={drainSingle}
                                                                label={t(drainSingle)}
                                                            />
                                                        )}
                                                    </>
                                                );
                                            } else if (x === hasWaterproofMembraneBelowDrain) {
                                                el = (
                                                    <Switch
                                                        defaultChecked={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench] as boolean}
                                                        onChange={e => handleChangeDrainageTrenches(e, i)}
                                                        name={x}
                                                        label={t(x)}
                                                    />
                                                );
                                            } else if (x == hasDrainagePiping) {
                                                el = (
                                                    <>
                                                        <Switch
                                                            disabled
                                                            hidden={handleHidden(x, i)}
                                                            defaultChecked={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench] as boolean}
                                                            name={x}
                                                            label={t(x)}
                                                        />
                                                        {formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench] && (
                                                            <>
                                                                <DropDown
                                                                    label={t(diameter, {
                                                                        unitOfMeasure:
                                                                            drainPipeDiameterPD[diameter as TInputDrainPipeDiameter]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                                    })}
                                                                    labelId={`${diameter}-label`}
                                                                    id={diameter}
                                                                    name={diameter}
                                                                    hidden={handleHidden(diameter, i)}
                                                                    value={drainPipeDiameters?.findIndex(
                                                                        x =>
                                                                            x.diameter == formik.values.inputPipeDiameter[i].diameter &&
                                                                            x.internalDiameter == formik.values.inputPipeDiameter[i].internalDiameter,
                                                                    )}
                                                                    onChange={e => handleChangePipeDiameter(e, i)}>
                                                                    {drainPipeDiameters?.map((x, index) => (
                                                                        <MenuItem key={x.diameter} value={index}>
                                                                            {x.diameter}
                                                                        </MenuItem>
                                                                    ))}
                                                                </DropDown>
                                                                <Input
                                                                    isNumber
                                                                    disabled
                                                                    hidden={handleHidden(internalDiameter, i)}
                                                                    minValue={inputRangeDrainageTrench()[x]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                                    maxValue={inputRangeDrainageTrench()[x]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                                    name={internalDiameter}
                                                                    value={formik.values.inputPipeDiameter[i].internalDiameter}
                                                                    id={internalDiameter}
                                                                    label={t(internalDiameter, {
                                                                        unitOfMeasure:
                                                                            drainPipeDiameterPD[internalDiameter as TInputDrainPipeDiameter]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                                    })}
                                                                />
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            } else if (x == drainPosition) {
                                                el = (
                                                    <DropDown
                                                        label={t(x)}
                                                        labelId={`${x}-label`}
                                                        id={x}
                                                        name={x}
                                                        value={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench]}
                                                        onChange={e => handleChangeDrainageTrenches(e, i)}>
                                                        <MenuItem value={EPosition.Vertical}>{t('vertical')}</MenuItem>
                                                        <MenuItem value={EPosition.Horizontal}>{t('horizontal')}</MenuItem>
                                                    </DropDown>
                                                );
                                            } else if (x == numberOfDrains) {
                                                el = (
                                                    <DropDown
                                                        label={t(x)}
                                                        labelId={`${x}-label`}
                                                        id={x}
                                                        name={x}
                                                        value={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench]}
                                                        onChange={e => handleChangeDrainageTrenches(e, i)}>
                                                        {[1, 2, 3].map(x => (
                                                            <MenuItem key={x} value={x}>
                                                                {x}
                                                            </MenuItem>
                                                        ))}
                                                    </DropDown>
                                                );
                                            } else if (x == hasDrainageChimney) {
                                                el = (
                                                    <>
                                                        <Switch
                                                            defaultChecked={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench] as boolean}
                                                            onChange={e => handleChangeDrainageTrenches(e, i)}
                                                            name={x}
                                                            label={t(x)}
                                                        />
                                                        {formik.values.inputDrainageTrenches[i].hasDrainageChimney && (
                                                            <>
                                                                <Stack variant='bottomLineTextGreen'>{t('drainageChimneyType', { type: i == 0 ? t('primary') : t('secondary') })}</Stack>
                                                                {Object.keys(formik.values.inputDrainageChimney[i]).map(x => {
                                                                    if (x == configuration) {
                                                                        return (
                                                                            <Stack key={x}>
                                                                                <DropDown
                                                                                    label={t(x)}
                                                                                    labelId={`${x}-label`}
                                                                                    id={x}
                                                                                    name={x}
                                                                                    value={formik.values.inputDrainageChimney[i][x as TInputDrainageChimney]}
                                                                                    onChange={e => handleChangeDrainageChimney(e, i)}>
                                                                                    <MenuItem value={EPosition.Vertical}>{t('vertical')}</MenuItem>
                                                                                    <MenuItem value={EPosition.Horizontal}>{t('horizontal')}</MenuItem>
                                                                                </DropDown>
                                                                            </Stack>
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <Stack key={x}>
                                                                                <Input
                                                                                    hidden={handleHidden(x, i)}
                                                                                    disabled={handleDisabled(x, i)}
                                                                                    value={formik.values.inputDrainageChimney[i][x as TInputDrainageChimney]}
                                                                                    id={x}
                                                                                    minValue={inputRangeDrainageTrench()[x]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                                                    maxValue={inputRangeDrainageTrench()[x]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                                                    isNumber={typeof formik.values.inputDrainageChimney[i][x as TInputDrainageChimney] == 'number'}
                                                                                    label={t(x, {
                                                                                        unitOfMeasure:
                                                                                            drainageChimneyPD[x as TInputDrainageChimney]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!],
                                                                                    })}
                                                                                    name={x}
                                                                                    onChange={(e: any) => handleChangeDrainageChimney(e, i)}
                                                                                    textTooltip={handleTextTooltip(x as TInputDrainageChimney)}
                                                                                />
                                                                            </Stack>
                                                                        );
                                                                    }
                                                                })}
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            } else {
                                                el = (
                                                    <Input
                                                        hidden={handleHidden(x, i)}
                                                        disabled={handleDisabled(x, i)}
                                                        value={formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench]}
                                                        id={x}
                                                        minValue={inputRangeDrainageTrench()[x]?.min.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                        maxValue={inputRangeDrainageTrench()[x]?.max.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!]}
                                                        isNumber={typeof formik.values.inputDrainageTrenches[i][x as TInputDrainageTrench] == 'number'}
                                                        label={t(x, { unitOfMeasure: drainageTrenchPD[x as TInputDrainageTrench]?.unitOfMeasure[project.data?.projectInfo.unitOfMeasure!] })}
                                                        name={x}
                                                        onChange={(e: any) => handleChangeDrainageTrenches(e, i)}
                                                        textTooltip={handleTextTooltip(x as TInputDrainageTrench)}
                                                    />
                                                );
                                            }

                                            return <Stack key={x}>{el}</Stack>;
                                        })}
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                            {i == 0 && (
                                <Stack paddingX={'16px'}>
                                    <Switch
                                        checked={hasSecondaryDrainageTrench}
                                        onChange={handleChangeHasSecondaryDrainageTrench}
                                        name={hasSecondaryDrainageTrenchKey}
                                        label={t(hasSecondaryDrainageTrenchKey)}
                                    />
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </AccordionDetails>
            </Accordion>
            <ModalProduct onClose={handleCloseModal} open={openModal} drainageTrenchType={drainageTrenchType} />
        </>
    );
};

export default InputDrainageTrench;
